import { useRef, useState, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useTenant } from '@/contexts'
import { useReportParams } from '@/lib/reports/space-yield/use-report-params'
import { useSpaceYieldReport } from '@/hooks/reports/useSpaceYieldReport'
import { exportCsv } from '@/lib/reports/space-yield/export-csv'
import { SpaceYieldControlBar } from '@/components/reports/space-yield/SpaceYieldControlBar'
import { SpaceYieldKpiCards } from '@/components/reports/space-yield/SpaceYieldKpiCards'
import { BuildingBars } from '@/components/reports/space-yield/BuildingBars'
import { UnitDetailTable } from '@/components/reports/space-yield/UnitDetailTable'
import type { UnitStatus } from '@/lib/reports/space-yield/types'

export function SpaceYieldPage() {
  const { currentLocation } = useTenant()
  const { params, setParams, clearStatus, setColumns } = useReportParams()
  const groupRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const [expandGroupKey, setExpandGroupKey] = useState<string | null>(null)

  const { data: report, isLoading, error } = useSpaceYieldReport(
    currentLocation?.id,
    params,
  )

  const handleExport = useCallback(() => {
    if (!report) return
    exportCsv(report.units, params.period, params.columns)
  }, [report, params.period, params.columns])

  const handleStatusClick = useCallback(
    (status: UnitStatus | undefined) => {
      if (status === undefined) {
        clearStatus()
      } else {
        setParams({ status })
      }
    },
    [setParams, clearStatus],
  )

  const handleGroupClick = useCallback((groupKey: string) => {
    // Auto-expand the group, then scroll to it
    setExpandGroupKey(groupKey)
    // Use a short delay to allow expansion render before scrolling
    requestAnimationFrame(() => {
      const el = groupRefs.current[groupKey]
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [])

  const periodDisplay = format(parseISO(`${params.period}-01`), 'MMMM yyyy')

  if (!currentLocation) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-slate-500">Select a location to view the Space Yield Report</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-slate-50/30">
      <SpaceYieldControlBar
        params={params}
        onParamsChange={setParams}
        onClearStatus={clearStatus}
        onExport={handleExport}
        unitTypeCounts={report?.unitTypeCounts ?? {}}
        onColumnsChange={setColumns}
        hasFuelData={report?.has_fuel_data}
      />

      <div className="flex-1 overflow-auto p-6 md:p-8 pt-6 space-y-8 max-w-[1600px] mx-auto w-full">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Space Yield</h1>
          <p className="text-slate-500">
            {currentLocation.name} · {periodDisplay}
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-red-700 font-medium">Failed to load report data</p>
            <p className="text-red-500 text-sm mt-1">{error.message}</p>
          </div>
        )}

        {/* Report Content */}
        {report && !isLoading && (
          <>
            {report.units.length === 0 ? (
              <EmptyState locationName={currentLocation.name} />
            ) : (
              <>
                <SpaceYieldKpiCards
                  data={report.kpi}
                  isMtd={report.is_mtd}
                  activeStatus={params.status}
                  onStatusClick={handleStatusClick}
                  streams={params.streams}
                />

                {params.group_by === 'building' && (
                  <BuildingBars
                    groups={report.groups}
                    onGroupClick={handleGroupClick}
                    streams={params.streams}
                  />
                )}

                <UnitDetailTable
                  groups={report.groups}
                  hasTransientData={report.has_transient_data}
                  hasFuelData={report.has_fuel_data}
                  groupBy={params.group_by}
                  visibleColumns={params.columns}
                  streams={params.streams}
                  groupRefs={groupRefs}
                  expandGroupKey={expandGroupKey}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function EmptyState({ locationName }: { locationName: string }) {
  return (
    <div className="rounded-lg border bg-white shadow-sm p-12 text-center space-y-4">
      <div className="text-4xl">📊</div>
      <h3 className="text-lg font-semibold text-slate-900">No yield data yet</h3>
      <p className="text-slate-500 max-w-md mx-auto">
        {locationName} has no lease contracts or transient stays for this period.
        Add contracts or log transient stays to see space yield analytics.
      </p>
    </div>
  )
}
